import React, { useEffect, useState } from 'react'
import { useIsFetching, useIsMutating } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { sessionLayoutSelector } from '../../redux/session/contract/selectors'
import { currentUserHomepageSelector } from '../../redux/session/currentUser/selectors'
import Logo from '../../components/Misc/Logo'
import Wordmark from '../../components/Misc/Wordmark'
import SantaHat, { isChristmasEve } from '../../components/EasterEgg/SantaHat'
import LoadingLogo from './LoadingLogo'

/** Positioning context for decorations on the logo mark, e.g. the Christmas Eve hat. */
const LogoMark = styled.span`
  position: relative;
  display: inline-flex;
  flex: none;
`

const LogoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  color: unset;

  img {
    flex: none;
    height: 32px;
  }

  img.wordmark {
    height: 22px;
  }

  /* Text wordmarks (e.g. the default "finadeck") */
  span.wordmark {
    font-size: 22px;
    font-weight: 600;
    white-space: nowrap;
  }

  /*
   * Wide images shrink to fit the sider instead of being cut off; next to a wordmark the logo mark keeps
   * its size. Height stays fixed because SVGs with only a viewBox have no intrinsic size to scale from.
   */
  img:only-child,
  img.wordmark {
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
    object-fit: contain;
    object-position: left center;
  }

  img:only-child {
    object-position: center;
  }
`

/** Delay before the logo starts animating, so cache hits and quick refetches don't flicker. */
const LOADING_DELAY_MS = 200

const useDelayedLoading = () => {
  const pending = useIsFetching() + useIsMutating() > 0
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!pending) {
      setLoading(false)
      return undefined
    }
    const timeout = setTimeout(() => setLoading(true), LOADING_DELAY_MS)
    return () => clearTimeout(timeout)
  }, [pending])

  return loading
}

interface AppLogoProps {
  /** Collapsed sider only has room for the logo mark, not the wordmark. */
  collapsed?: boolean
}

const AppLogo: React.FC<AppLogoProps> = ({ collapsed }) => {
  const homepage = useSelector(currentUserHomepageSelector)
  const whiteLabel = useSelector(sessionLayoutSelector)
  const loading = useDelayedLoading()

  return (
    <LogoLink to={homepage} id="logo">
      {whiteLabel && (
        <>
          {whiteLabel === 'app' ? (
            <LogoMark>
              <LoadingLogo loading={loading} />
              {/* Only on our own logo, never on a customer's white-label one */}
              {isChristmasEve() && <SantaHat />}
            </LogoMark>
          ) : (
            <Logo type={whiteLabel} />
          )}
          {!collapsed && <Wordmark type={whiteLabel} />}
        </>
      )}
    </LogoLink>
  )
}

export default AppLogo
